import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';

@Component({
  selector: 'app-received-raw-material',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './received-raw-material.component.html',
  styleUrls: ['./received-raw-material.component.css']
})
export class ReceivedRawMaterialComponent implements OnChanges {

  @Input() data: any[] = [];

  activeTab: 'day' | 'week' | 'month' | 'year' = 'month';
  activeMaterialTab: 'metal' | 'stone' = 'metal';

  activeMaterialTitle: 'Metal' | 'Stone' = 'Metal';

  currentDate: Date = new Date();
  displayDate: string = '';
  displayUnit: 'Gm' | 'Pcs' = 'Gm';

  totalIssued = 0;
  totalSubmitted = 0;
  totalAvailable = 0;

  private datePipe = new DatePipe('en-US');

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (!this.data || this.data.length === 0) {
        this.resetTotals();
        this.updateDisplayDate();
        return;
      }

      this.processData();
    }
  }

  processData(): void {

    if (!this.data?.length) {
      this.resetTotals();
      return;
    }

    const filteredOrders = this.filterOrdersByDate();

    this.updateDisplayDate();
    this.calculateTotals(filteredOrders);
  }

  private filterOrdersByDate(): any[] {

    const start = new Date(this.currentDate);
    const end = new Date(this.currentDate);

    switch (this.activeTab) {

      case 'day':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;

      case 'week':
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1);

        start.setDate(diff);
        start.setHours(0, 0, 0, 0);

        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;

      case 'month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);

        end.setMonth(start.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;

      case 'year':
        start.setMonth(0, 1);
        start.setHours(0, 0, 0, 0);

        end.setMonth(11, 31);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return this.data.filter(order => {

      const orderDate = new Date(order.created_at);

      return orderDate >= start && orderDate <= end;
    });
  }

  private calculateTotals(orders: any[]): void {

    this.resetTotals();

    const isMetal = this.activeMaterialTab === 'metal';

    const issuedKey = isMetal ? 'ordc_metal_details' : 'ordc_stone_details';
    const submittedKey = isMetal
      ? 'ordc_submitted_metal_details'
      : 'ordc_submitted_stone_details';

    const valueKey = isMetal ? 'total_silver' : 'total_stone';

    orders.forEach(order => {

      const issued = this.sumValue(order?.[issuedKey], valueKey);
      const submitted = this.sumValue(order?.[submittedKey], valueKey);

      this.totalIssued += issued;
      this.totalSubmitted += submitted;
    });

    // calculate available automatically
    this.totalAvailable = this.totalIssued - this.totalSubmitted;
  }

  private sumValue(details: any, key: string): number {

    if (!details) return 0;

    // API returns array
    if (Array.isArray(details)) {

      return details.reduce(
        (sum, item) => sum + (parseFloat(item?.[key]) || 0),
        0
      );
    }

    // API returns object
    if (typeof details === 'object') {

      return parseFloat(details?.[key]) || 0;
    }

    return 0;
  }

  private resetTotals(): void {

    this.totalIssued = 0;
    this.totalSubmitted = 0;
    this.totalAvailable = 0;
  }

  setActiveTab(tab: 'day' | 'week' | 'month' | 'year'): void {

    this.activeTab = tab;

    this.processData();
  }

  setActiveMaterialTab(material: 'metal' | 'stone'): void {

    this.activeMaterialTab = material;

    this.displayUnit = material === 'metal' ? 'Gm' : 'Pcs';

    this.activeMaterialTitle =
      (material.charAt(0).toUpperCase() + material.slice(1)) as
      | 'Metal'
      | 'Stone';

    this.processData();
  }

  navigateDate(direction: 'prev' | 'next'): void {

    const multiplier = direction === 'prev' ? -1 : 1;

    switch (this.activeTab) {

      case 'day':
        this.currentDate.setDate(this.currentDate.getDate() + multiplier);
        break;

      case 'week':
        this.currentDate.setDate(this.currentDate.getDate() + 7 * multiplier);
        break;

      case 'month':
        this.currentDate.setMonth(this.currentDate.getMonth() + multiplier);
        break;

      case 'year':
        this.currentDate.setFullYear(this.currentDate.getFullYear() + multiplier);
        break;
    }

    this.currentDate = new Date(this.currentDate);

    this.processData();
  }

  private updateDisplayDate(): void {

    switch (this.activeTab) {

      case 'day':
        this.displayDate =
          this.datePipe.transform(this.currentDate, 'dd-MM-yyyy') || '';
        break;

      case 'week':

        const startOfWeek = new Date(this.currentDate);

        const day = startOfWeek.getDay();

        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);

        startOfWeek.setDate(diff);

        const endOfWeek = new Date(startOfWeek);

        endOfWeek.setDate(startOfWeek.getDate() + 6);

        this.displayDate =
          `${this.datePipe.transform(startOfWeek, 'dd MMM')} - ` +
          `${this.datePipe.transform(endOfWeek, 'dd MMM yyyy')}`;
        break;

      case 'month':
        this.displayDate =
          this.datePipe.transform(this.currentDate, 'MMMM yyyy') || '';
        break;

      case 'year':
        this.displayDate =
          this.datePipe.transform(this.currentDate, 'yyyy') || '';
        break;
    }
  }
}